import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const Cell = ({ label, value, tone = "", testid, sub }) => (
  <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-3 sm:p-4" data-testid={testid}>
    <p className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-400">{label}</p>
    <p className={`font-mono text-lg sm:text-2xl mt-1.5 tracking-tight ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-zinc-400 mt-1 font-mono">{sub}</p>}
  </div>
);

const Toggle = ({ open, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    className={`w-full flex items-center justify-between border border-zinc-800 bg-zinc-900/40 rounded-sm px-3 py-2.5 text-left transition-colors hover:bg-zinc-900/70 ${className}`}
    data-testid="summary-toggle"
  >
    <span className="text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-400">
      Day summary
    </span>
    <ChevronDown
      className={`h-4 w-4 text-zinc-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
    />
  </button>
);

export const SummaryCards = ({ summary, collapsible = false, mobileOnly = false, storageKey }) => {
  const [open, setOpen] = useState(() => {
    if (mobileOnly) return false;
    if (!storageKey) return true;
    return localStorage.getItem(storageKey) !== "0";
  });

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, open ? "1" : "0");
  }, [open, storageKey]);

  if (!summary) return null;
  const pnl = summary.realised_pnl;

  const grid = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="daily-summary">
      <Cell
        label="Trades Today"
        value={
          summary.max_trades_per_day > 0
            ? `${summary.trades_used_today ?? summary.trades_today} / ${summary.max_trades_per_day}`
            : summary.trades_today
        }
        tone={summary.trade_limit_hit ? "text-amber-300" : "text-zinc-50"}
        sub={
          summary.daily_profit_target > 0
            ? `Target $${Number(summary.daily_profit_target).toFixed(2)}${
                summary.profit_target_hit ? " · reached" : ""
              }`
            : undefined
        }
        testid="summary-trades"
      />
      <Cell
        label="Realised P&L Today"
        value={`${pnl >= 0 ? "+" : "-"}$${Math.abs(pnl).toFixed(2)}`}
        tone={pnl > 0 ? "text-emerald-400" : pnl < 0 ? "text-red-400" : "text-zinc-50"}
        sub={`Floating ${summary.floating_pnl >= 0 ? "+" : "-"}$${Math.abs(
          summary.floating_pnl
        ).toFixed(2)}`}
        testid="summary-pnl"
      />
      <Cell
        label="Loss Limit Left"
        value={`$${summary.loss_limit_remaining.toFixed(2)}`}
        tone={summary.loss_limit_hit ? "text-red-400" : "text-zinc-50"}
        sub={
          summary.loss_limit_mode === "trailing"
            ? `of $${summary.loss_limit_allowance.toFixed(2)} · base $${summary.daily_loss_limit.toFixed(
                2
              )} + booked $${(summary.loss_limit_booked ?? 0).toFixed(2)}`
            : `of $${summary.daily_loss_limit.toFixed(2)} · fixed`
        }
        testid="summary-loss-limit"
      />
      <Cell
        label="Open / Max"
        value={`${summary.open_trades} / ${summary.max_open_trades}`}
        tone={summary.open_trades >= summary.max_open_trades ? "text-amber-300" : "text-zinc-50"}
        sub={`Window ${summary.window}`}
        testid="summary-open-trades"
      />
    </div>
  );

  if (!collapsible) return grid;

  // Mobile-only mode: collapsed by default on small screens, always expanded from sm up.
  if (mobileOnly) {
    return (
      <div className="flex flex-col gap-3">
        <Toggle open={open} onClick={() => setOpen((v) => !v)} className="sm:hidden" />
        <div className={open ? "" : "hidden sm:block"}>{grid}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Toggle open={open} onClick={() => setOpen((v) => !v)} />
      {open && grid}
    </div>
  );
};
